import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { FrontendErrorNotifierService } from '../core/errors/frontend-error-notifier.service';

@Injectable()
export class GlobalHttpErrorInterceptor implements HttpInterceptor {
  private readonly noisyExternalErrorCounts = new Map<string, number>();
  private readonly noisyExternalMaxGlobalNotifications = 2;

  constructor(private notifier: FrontendErrorNotifierService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      retry({
        count: 2,
        delay: (error: HttpErrorResponse) => {
          if (this.shouldRetryTransientRequest(req, error)) {
            return timer(1000);
          }
          return throwError(() => error);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.shouldNotify(req, error)) {
          this.notifier.showError({
            status: error.status || 'RED',
            title: this.buildTitle(error),
            userMessage: this.buildUserMessage(error),
            subtitle: 'La operación no se completó. Si el problema se repite, copie el detalle técnico y repórtelo a sistemas.',
            detail: this.buildTechnicalDetail(req, error),
          });
        }

        return throwError(() => error);
      })
    );
  }

  private shouldRetryTransientRequest(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return req.url.includes('/users/login/branches') && [0, 502, 503, 504].includes(error?.status || 0);
  }

  private shouldNotify(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    if (!error) return false;
    if (req.url.includes('/assets/')) return false;
    if (error.status === 401) return false;
    if (this.isLoginRateLimitRequest(req, error)) return false;
    if (this.isSilentValidationRequest(req, error)) return false;
    if (this.isNoisyExternalRequest(req)) {
      const key = this.buildNoisyExternalErrorKey(req, error);
      const count = (this.noisyExternalErrorCounts.get(key) || 0) + 1;
      this.noisyExternalErrorCounts.set(key, count);
      return count <= this.noisyExternalMaxGlobalNotifications;
    }
    return true;
  }

  private isLoginRateLimitRequest(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return req.url.includes('/users/login') && error.status === 429;
  }

  private isSilentValidationRequest(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return (
      req.url.includes('/persons/doctors/search') && error.status === 400
    ) || (
      req.url.includes('/company/price-requests/status') &&
      [400, 403, 404].includes(error.status)
    );
  }

  private isNoisyExternalRequest(req: HttpRequest<any>): boolean {
    return [
      '/transactions/electronic-documents/send-sri',
      '/persons/patients/identity/lookup',
    ].some((path) => req.url.includes(path));
  }

  private buildNoisyExternalErrorKey(req: HttpRequest<any>, error: HttpErrorResponse): string {
    const payload = error.error;
    return [
      req.method,
      req.url.split('?')[0],
      error.status || 'RED',
      payload?.errorCode ?? payload?.code ?? '',
      this.extractPayloadMessage(payload) || error.message || '',
    ].join('|');
  }

  private buildTitle(error: HttpErrorResponse): string {
    if (error.status === 403) return 'No tiene permiso para realizar esta operación';
    if (error.status === 0) return 'No hay conexión con el servidor';
    if (error.status >= 500) return 'Error interno del servidor';
    return 'No fue posible completar la operación';
  }

  private buildUserMessage(error: HttpErrorResponse): string {
    const payload = error.error;
    const payloadMessage = this.extractPayloadMessage(payload);

    if (payloadMessage) return payloadMessage;
    if (error.status === 0) return 'Revise su conexión o confirme que el backend esté disponible.';
    if (error.status === 403) return 'La sesión actual no tiene el permiso requerido para esta acción.';
    if (error.status >= 500) return 'El servidor respondió con un error inesperado.';
    return error.message || 'Ocurrió un error inesperado.';
  }

  private extractPayloadMessage(payload: any): string {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (payload.message) return String(payload.message);
    if (payload.error?.message) return String(payload.error.message);
    if (payload.errors?.reason) return String(payload.errors.reason);
    if (payload.error) return String(payload.error);
    return '';
  }

  private buildTechnicalDetail(req: HttpRequest<any>, error: HttpErrorResponse): string {
    const payload = error.error;
    const detail = {
      method: req.method,
      url: req.urlWithParams,
      status: error.status,
      statusText: error.statusText,
      errorCode: payload?.errorCode ?? payload?.code ?? null,
      message: this.extractPayloadMessage(payload) || error.message,
      errors: payload?.errors ?? null,
    };

    try {
      return JSON.stringify(detail, null, 2);
    } catch {
      return String(error.message || error);
    }
  }
}
